import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FaLightbulb, FaFileAlt, FaChartLine, FaHandshake, FaCalculator, FaClipboardCheck } from "react-icons/fa";
import "bootstrap/dist/css/bootstrap.min.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import styled, { keyframes } from "styled-components";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(50px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ServiceCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  margin-bottom: 30px;
  animation: ${fadeUp} 0.5s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  font-size: 42px;
  color: ${props => props.color || "#0d6efd"};
  margin-bottom: 20px;
`;

const ServiceTitle = styled.h4`
  font-weight: 700;
  margin-bottom: 15px;
  color: #333;
`;

const ServiceDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${props => props.background || "white"};
`;

const ServiceListItem = styled.div`
  display: flex;
  margin-bottom: 15px;
  opacity: 0;
  transform: translateY(30px);
  animation: ${fadeUp} 0.6s ease-in-out forwards;
  animation-delay: ${({ delay }) => delay || "0s"};
`;

const ServiceListIcon = styled.div`
  margin-right: 15px;
  color: #0d6efd;
  flex-shrink: 0;
  margin-top: 3px;
`;

const ServiceListContent = styled.div`
  flex-grow: 1;
`;

const ServiceListTitle = styled.h5`
  font-weight: 700;
  margin-bottom: 5px;
  color: #333;
`;

const ServiceListDescription = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 0;
`;

const PrimoEntrepreneurs = () => {
    const [setIsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            const faqSection = document.getElementById("faq-section");
            if (faqSection) {
                const rect = faqSection.getBoundingClientRect();
                if (rect.top < window.innerHeight * 0.8 && rect.bottom > 0) {
                    setIsVisible(true);
                } else {
                    setIsVisible(false);
                }
            }
        };

        window.addEventListener("scroll", handleScroll);
        handleScroll();
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    const servicesData = [
        {
            icon: <FaLightbulb />,
            title: "Validation et Structuration",
            description: "Étude de marché, choix de la forme juridique, élaboration du business plan pour valider votre projet.",
            color: "#0d6efd"
        },
        {
            icon: <FaFileAlt />,
            title: "Formalités Simplifiées",
            description: "Prise en charge des démarches administratives, rédaction des statuts et immatriculation de votre entreprise.",
            color: "#198754"
        },
        {
            icon: <FaChartLine />,
            title: "Suivi et Développement",
            description: "Mise en place de la comptabilité, tableaux de bord, conseil personnalisé pour piloter votre activité.",
            color: "#dc3545"
        },
        {
            icon: <FaHandshake />,
            title: "Offres Spéciales Primo",
            description: "Packs à tarifs préférentiels, consultations gratuites et outils en ligne pour démarrer sereinement.",
            color: "#6610f2"
        }
    ];

    const serviceDetails = {
        avantCreation: [
            {
                title: "Étude de marché",
                description: "Analyse de votre marché, de la concurrence, et des tendances. Validation du potentiel de votre projet."
            },
            {
                title: "Choix de la forme juridique",
                description: "Micro-entreprise, entreprise individuelle, SASU, SARL, SAS... Nous vous aidons à choisir la structure la plus adaptée à votre situation et à vos objectifs."
            },
            {
                title: "Élaboration du business plan",
                description: "Construction d'un business plan solide et convaincant, comprenant présentation du projet, analyse du marché, stratégie commerciale et prévisionnel financier."
            },
            {
                title: "Recherche de financements",
                description: "Identification des sources de financement, préparation des dossiers de demande, accompagnement aux rendez-vous avec les partenaires financiers."
            },
            {
                title: "Conseils personnalisés",
                description: "Réponse à toutes vos questions sur la création d'entreprise (réglementation, formalités, etc.)."
            }
        ],
        pendantCreation: [
            {
                title: "Prise en charge des formalités administratives",
                description: "En partenariat avec un office notarial, nous prenons en charge la rédaction des statuts, l'immatriculation au Registre du Commerce et du crédit mobilier, etc."
            },
            {
                title: "Démarches auprès des organismes sociaux et fiscaux",
                description: "Affiliation aux caisses de retraite, déclaration d'existence, et autres démarches obligatoires."
            }
        ],
        apresCreation: [
            {
                title: "Mise en place de votre comptabilité",
                description: "Choix des outils, organisation administrative, formation à la saisie comptable (si vous souhaitez gérer une partie de votre comptabilité)."
            },
            {
                title: "Tenue de la comptabilité",
                description: "Saisie de vos opérations, établissement des déclarations de TVA, rapprochements bancaires."
            },
            {
                title: "Gestion de la paie et du social",
                description: "Établissement des bulletins de paie, déclarations sociales, gestion des contrats de travail."
            },
            {
                title: "Déclarations fiscales",
                description: "Établissement et télétransmission de vos déclarations (impôt sur le revenu, impôt sur les sociétés, etc.)."
            },
            {
                title: "Tableaux de bord et suivi d'activité",
                description: "Mise en place d'indicateurs clés de performance (KPI) pour suivre votre activité et prendre les bonnes décisions."
            },
            {
                title: "Conseil et accompagnement personnalisé",
                description: "Un expert-comptable dédié répond à vos questions et vous conseille à chaque étape de votre développement."
            },
            {
                title: "Conseil juridique",
                description: "En partenariat avec des avocats : contrats, CGV, etc."
            }
        ],
        offresSpeciales: [
            {
                title: "Pack Création d'Entreprise",
                description: "Une offre complète et à tarif préférentiel pour vous accompagner dans toutes les étapes de la création."
            },
            {
                title: "Tarifs réduits les premiers mois",
                description: "Pour vous aider à démarrer sereinement."
            },
            {
                title: "Consultations gratuites",
                description: "Des rendez-vous réguliers pour faire le point sur votre situation."
            },
            {
                title: "Accès à des outils en ligne",
                description: "Logiciel de facturation, application mobile, et autres outils pratiques."
            }
        ]
    };

    return (
        <>
            <Header />

            <section className="service-banner d-flex align-items-center">
                <div className="container-fluid px-4 px-md-5 text-white">
                    <div className="row">
                        <div className="col-lg-12">
                            <h1 className="fw-bold display-4 mb-3">L'Expertise Comptable pour Réussir Votre Première Entreprise</h1>
                            <p className="fs-5 mb-4">De l'Idée au Succès : Nous Vous Accompagnons à Chaque Étape</p>
                            <nav aria-label="breadcrumb">

                            </nav>
                        </div>
                    </div>
                </div>
            </section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="fw-bold custom-title text-center mb-5">
                                CINEF-EC comprend les défis spécifiques des primo-entrepreneurs
                            </h2>
                            <p className="text-muted text-center fs-5 mb-5">
                                Vous avez une idée, un projet, l'envie de créer votre entreprise ?
                                C'est une aventure passionnante, mais aussi semée d'embûches. CINEF-EC
                                est là pour vous aider à transformer votre rêve en réalité. Nous sommes
                                spécialisés dans l'accompagnement des primo-entrepreneurs et nous vous
                                offrons un soutien complet, de la conception de votre projet à sa
                                concrétisation et à son développement.
                            </p>
                        </div>
                    </div>

                    <div className="row">
                        {servicesData.map((service, index) => (
                            <div className="col-lg-3 col-md-6" key={index}>
                                <ServiceCard delay={`${0.1 + index * 0.1}s`}>
                                    <ServiceIcon color={service.color}>
                                        {service.icon}
                                    </ServiceIcon>
                                    <ServiceTitle>{service.title}</ServiceTitle>
                                    <ServiceDescription>{service.description}</ServiceDescription>
                                </ServiceCard>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section background="#f8f9fa">
                <div className="container-fluid px-4 px-md-5">
                    <div className="row align-items-center">
                        <div className="col-lg-12">
                            <h3 className="fw-bold custom-title mb-4">Nous vous accompagnons à chaque phase cruciale de votre parcours entrepreneurial</h3>
                            <p className="mb-4">
                                La création d'une entreprise est un parcours jalonné d'étapes importantes. Notre cabinet d'expertise comptable
                                vous propose un accompagnement sur mesure, adapté à vos besoins et à votre situation.
                            </p>
                            <p className="mb-4">
                                Notre objectif est de vous libérer des contraintes administratives, comptables et juridiques pour vous permettre
                                de vous concentrer sur l'essentiel : le développement de votre activité.
                            </p>
                            <div className="d-flex mt-4">
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-primary d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaLightbulb className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Avant la création</span>
                                </div>
                                <div className="d-flex align-items-center me-4">
                                    <div className="rounded-circle bg-success d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaFileAlt className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Pendant la création</span>
                                </div>
                                <div className="d-flex align-items-center">
                                    <div className="rounded-circle bg-danger d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px' }}>
                                        <FaChartLine className="text-white fs-4" />
                                    </div>
                                    <span className="ms-2 fw-bold">Après la création</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">1. Avant la Création : Valider et Structurer Votre Projet</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.avantCreation.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`avant-creation-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">2. Au Moment de la Création : Formalités Simplifiées</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.pendantCreation.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`pendant-creation-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">3. Après la Création : Lancement et Pilotage de Votre Activité</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.apresCreation.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`apres-creation-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>

                    <div className="row mb-5">
                        <div className="col-12">
                            <h3 className="fw-bold custom-title mb-4">4. Offres Spéciales Primo</h3>
                        </div>
                    </div>

                    <div className="row mb-5">
                        {serviceDetails.offresSpeciales.map((item, index) => (
                            <div className="col-md-6 mb-4" key={`offres-speciales-${index}`}>
                                <ServiceListItem delay={`${index * 0.1}s`}>
                                    <ServiceListIcon>
                                        <i className="fas fa-check-circle fa-lg"></i>
                                    </ServiceListIcon>
                                    <ServiceListContent>
                                        <ServiceListTitle>{item.title}</ServiceListTitle>
                                        <ServiceListDescription>{item.description}</ServiceListDescription>
                                    </ServiceListContent>
                                </ServiceListItem>
                            </div>
                        ))}
                    </div>
                </div>
            </Section>

            <Section>
                <div className="container-fluid px-4 px-md-5">
                    <div className="row justify-content-center mb-5">
                        <div className="col-12 col-md-10 col-lg-8">
                            <div className="call-to-action p-5 rounded-3 text-center">
                                <h3 className="fw-bold text-white mb-3">Lancez-vous en toute confiance avec CINEF-EC !</h3>
                                <p className="text-white fs-5 mb-4">
                                    Contactez-nous dès aujourd'hui pour une consultation gratuite et sans engagement. 
                                    Nous serons ravis de discuter de votre projet et de vous proposer un accompagnement sur mesure.
                                </p>
                                <a href="/contact" className="btn btn-light btn-lg fw-bold px-4">Demander un entretien gratuit</a>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            <Footer />

            <style>{`
        .service-banner {
          background: linear-gradient(to right, rgba(0, 138, 68, 0.85), rgba(220, 53, 69, 0.85)), 
                      url("/assets/images/entrepreneur-banner.jpg");
          background-size: cover;
          background-position: center;
          height: 400px;
          display: flex;
          align-items: center;
        }

        .breadcrumb {
          background: transparent;
          font-size: 18px;
        }

        .breadcrumb-item a {
          color: white;
          font-weight: 500;
        }

        .custom-title {
          position: relative;
          padding-bottom: 15px;
        }

        .custom-title:after {
          content: '';
          position: absolute;
          left: 0;
          bottom: 0;
          width: 60px;
          height: 3px;
          background: linear-gradient(to right, #008a44, #dc3545);
        }

        .text-center .custom-title:after {
          left: 50%;
          transform: translateX(-50%);
        }

        .custom-underline {
          height: 4px;
          width: 70px;
          background: linear-gradient(to right, #008a44, #dc3545);
          margin-top: 10px;
        }

        .call-to-action {
          background: linear-gradient(to right, #008a44, #dc3545);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        #faq-section {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s ease;
        }

        #faq-section.visible {
          opacity: 1;
          transform: translateY(0);
        }
        
        @media (max-width: 768px) {
          .service-banner {
            height: 350px;
          }
          
          .custom-title {
            font-size: 1.75rem;
          }
          
          .d-flex {
            flex-wrap: wrap;
          }
          
          .d-flex .d-flex {
            margin-bottom: 10px;
          }
        }

        .fa-check-circle {
          color: #0d6efd;
        }
      `}</style>
        </>
    );
};

export default PrimoEntrepreneurs;